import { useState, useEffect, useMemo, useCallback } from 'react';
import type { Move, ClawerContact } from '../../types';
import { movesAPI, contactsAPI } from '../../api/client';
import { useToast } from '../Toast';
import { WeekTracker, ChecklistItem } from '../ui';
import { MoveDetailSheet } from '../MoveDetailSheet';
import { ScheduledMessageComposer } from '../ScheduledMessageComposer';

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getEffectiveDate(move: Move): string | null {
  if (move.due_date) return move.due_date;
  if (move.follow_up_within_days) {
    const d = new Date(move.created_at);
    d.setDate(d.getDate() + move.follow_up_within_days);
    return toISO(d);
  }
  return null;
}

const URGENCY_ORDER: Record<string, number> = {
  overdue: 0,
  time_sensitive: 1,
  routine: 2,
};

function getUrgencyOrder(move: Move): number {
  if (!move.urgency) return 3;
  return URGENCY_ORDER[move.urgency] ?? 3;
}

// Loading skeleton row
function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border border-edge/50 animate-pulse">
      <div className="w-6 h-6 rounded-full bg-edge" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-edge rounded w-3/4" />
        <div className="h-2 bg-edge rounded w-1/2" />
      </div>
      <div className="w-4 h-4 rounded bg-edge" />
    </div>
  );
}

export function MovesScreen() {
  const { addToast } = useToast();
  const todayISO = toISO(new Date());

  const [selectedDate, setSelectedDate] = useState<string>(todayISO);
  const [moves, setMoves] = useState<Move[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMove, setSelectedMove] = useState<Move | null>(null);
  const [composingMove, setComposingMove] = useState<Move | null>(null);
  const [contacts, setContacts] = useState<ClawerContact[]>([]);

  useEffect(() => {
    if (!composingMove) return;
    contactsAPI.list({ limit: 200 }).then(setContacts).catch(console.error);
  }, [composingMove]);

  // Fetch all moves (no date filter — client groups by date)
  const fetchMoves = useCallback(async () => {
    try {
      const data = await movesAPI.list({ limit: 200 });
      setMoves(data);
    } catch (e) {
      console.error('Failed to fetch moves:', e);
      addToast('Failed to load moves', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchMoves();
  }, [fetchMoves]);

  // Group moves by effective date (due_date or created_at + follow_up_within_days)
  const groupedByDate = useMemo(() => {
    const map = new Map<string, Move[]>();
    for (const move of moves) {
      const date = getEffectiveDate(move);
      if (!date) continue;
      if (!map.has(date)) map.set(date, []);
      map.get(date)!.push(move);
    }
    return map;
  }, [moves]);

  // Move counts for WeekTracker dots
  const moveCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [date, dayMoves] of groupedByDate.entries()) {
      counts[date] = dayMoves.length;
    }
    return counts;
  }, [groupedByDate]);

  // Current day's moves
  const dayMoves = useMemo(() => {
    const all = groupedByDate.get(selectedDate) ?? [];
    const active = all
      .filter((m) => m.status === 'active')
      .sort((a, b) => getUrgencyOrder(a) - getUrgencyOrder(b));
    const completed = all.filter((m) => m.status === 'done' || m.status === 'skipped');
    return [...active, ...completed];
  }, [groupedByDate, selectedDate]);

  // Optimistic status update
  const handleStatusChange = useCallback(
    async (id: number, status: 'done' | 'skipped' | 'active') => {
      // Capture previous state for rollback
      const prevMoves = moves;

      // Optimistic update
      setMoves((prev) =>
        prev.map((m) =>
          m.id === id
            ? {
                ...m,
                status,
                completed_at: status === 'active' ? null : new Date().toISOString(),
              }
            : m
        )
      );

      // If the detail sheet's move was updated, update selectedMove too
      setSelectedMove((prev) =>
        prev && prev.id === id
          ? { ...prev, status, completed_at: status === 'active' ? null : new Date().toISOString() }
          : prev
      );

      try {
        const updated = await movesAPI.update(id, { status });
        // Sync with server response
        setMoves((prev) => prev.map((m) => (m.id === id ? updated : m)));
        setSelectedMove((prev) => (prev && prev.id === id ? updated : prev));
      } catch (e) {
        console.error('Failed to update move status:', e);
        // Revert optimistic update
        setMoves(prevMoves);
        setSelectedMove((prev) => (prev && prev.id === id ? prevMoves.find((m) => m.id === id) ?? prev : prev));
        addToast('Failed to update move. Please try again.', 'error');
      }
    },
    [moves, addToast]
  );

  // Toggle done/active from checklist
  const handleToggle = useCallback(
    (id: number) => {
      const move = moves.find((m) => m.id === id);
      if (!move) return;
      const newStatus = move.status === 'done' || move.status === 'skipped' ? 'active' : 'done';
      handleStatusChange(id, newStatus);
    },
    [moves, handleStatusChange]
  );

  const handleTap = useCallback((move: Move) => {
    setSelectedMove(move);
  }, []);

  const handleSheetClose = useCallback(() => {
    setSelectedMove(null);
  }, []);

  const today = toISO(new Date());
  const selectedDateLabel = selectedDate === today
    ? 'Today'
    : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

  return (
    <div className="relative w-full h-full flex flex-col bg-canvas overflow-hidden">
      {/* Header */}
      <header className="px-4 sm:px-6 pt-6 pb-2 flex-shrink-0">
        <h1 className="text-xl font-bold text-ink" data-ph-no-mask>Moves</h1>
        <p className="text-sm text-ink-secondary mt-0.5" data-ph-no-mask>{selectedDateLabel}</p>
      </header>

      {/* Week Tracker */}
      <div className="px-4 sm:px-6 py-3 flex-shrink-0">
        <WeekTracker
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          moveCounts={moveCounts}
        />
      </div>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto px-4 sm:px-6 pb-8 space-y-2">
        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-2 pt-2">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {/* Empty state */}
        {!loading && dayMoves.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="material-icons-round text-4xl text-ink-tertiary mb-3">task_alt</span>
            <p className="text-ink-secondary font-medium" data-ph-no-mask>No moves for this day</p>
            <p className="text-ink-tertiary text-sm mt-1 max-w-xs" data-ph-no-mask>
              Action items from your conversations will appear here
            </p>
          </div>
        )}

        {/* Move list */}
        {!loading && dayMoves.length > 0 && (
          <div className="space-y-2 pt-2">
            {dayMoves.map((move) => (
              <ChecklistItem
                key={move.id}
                move={move}
                onToggle={handleToggle}
                onTap={handleTap}
              />
            ))}
          </div>
        )}
      </main>

      {/* Move Detail Sheet */}
      <MoveDetailSheet
        move={selectedMove}
        isOpen={selectedMove !== null}
        onClose={handleSheetClose}
        onStatusChange={handleStatusChange}
        onCompose={(move) => {
          setComposingMove(move);
          setSelectedMove(null);
        }}
      />

      {composingMove && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setComposingMove(null)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-ink">Send Message</h3>
              <button onClick={() => setComposingMove(null)} className="text-ink-tertiary">
                <span className="material-icons-round" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <ScheduledMessageComposer
              contacts={contacts}
              prefillContactId={composingMove.contact_id}
              onCreated={() => setComposingMove(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MovesScreen;
